import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  makeStyles,
  TextField,
  Typography
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import WarningIcon from '@material-ui/icons/Warning';
import { useDispatch, useSelector } from 'react-redux';

import { createWarrantyTicket, getListWarrantyTickets } from 'src/features/deviceSlice';

const useStyles = makeStyles(() => ({
  errorBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '8px 12px',
    borderRadius: 6,
    backgroundColor: '#fdecea',
    border: '1px solid #f8d0d0',
    marginBottom: 16
  },
  errorText: {
    fontSize: 13,
    color: '#991b1b',
    lineHeight: 1.4
  }
}));

export default function CreateWarrantyTicketModal({ open, onClose, onSuccess }) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [faultySerial, setFaultySerial] = useState('');
  const [replacementSerial, setReplacementSerial] = useState('');
  const [note, setNote] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setFaultySerial('');
    setReplacementSerial('');
    setNote('');
    setError('');
    setLoading(false);
  }, [open]);

  const handleCreate = async () => {
    if (!faultySerial.trim()) {
      setError('Vui lòng nhập Serial thiết bị lỗi *');
      return;
    }

    setError('');
    setLoading(true);
    try {
      // 1. Kiểm tra xem Serial lỗi này đã có phiếu bảo hành nào đang tồn tại hay chưa
      try {
        const checkRes = await dispatch(
          getListWarrantyTickets({ serial: faultySerial.trim() })
        ).unwrap();
        const existingList = checkRes?.payload?.data || checkRes?.data || (Array.isArray(checkRes?.payload) ? checkRes.payload : []);
        
        const activeTicket = (existingList || []).find(
          t => t.faulty_serial === faultySerial.trim() && Number(t.status) !== -1
        );

        if (activeTicket) {
          setError(
            `Serial lỗi ${faultySerial.trim()} đã có phiếu bảo hành (${activeTicket.ticket_code || activeTicket.id}) đang tồn tại. Không thể tạo trùng phiếu!`
          );
          setLoading(false);
          return;
        }
      } catch (errCheck) {
        // Tiếp tục nếu không tra cứu được danh sách
      }

      // 2. Tiến hành tạo phiếu
      const payload = {
        faulty_serial: faultySerial.trim(),
        confirm: true
      };

      if (replacementSerial.trim()) {
        payload.replacement_serial = replacementSerial.trim();
      }
      if (note.trim()) {
        payload.note = note.trim();
      }

      const res = await dispatch(createWarrantyTicket(payload)).unwrap();
      const resPayload = res?.payload || res;
      onSuccess && onSuccess(resPayload);
      onClose && onClose();
    } catch (err) {
      const msg = typeof err === 'string'
        ? err
        : err?.message || 'Tạo phiếu bảo hành thất bại. Vui lòng kiểm tra lại.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const renderError = content => (
    <Box className={classes.errorBox}>
      <WarningIcon fontSize="small" style={{ color: '#d9042f', flexShrink: 0, marginTop: 1 }} />
      <Box className={classes.errorText}>{content}</Box>
    </Box>
  );

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography style={{ borderBottom: '1px solid #eee', padding: '14px 24px' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" style={{ fontWeight: 700, color: '#1a1f36', fontSize: 18 }}>
            + Tạo phiếu bảo hành
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            disabled={loading}
            style={{ color: '#666' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers style={{ padding: '20px 24px' }}>
        {/* Thông tin Serial & Ghi chú */}
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              style={{ fontSize: 13, color: '#555', marginBottom: 4, fontWeight: 600 }}
            >
              Serial thiết bị lỗi <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Nhập serial thiết bị lỗi"
              value={faultySerial}
              onChange={e => setFaultySerial(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              style={{ fontSize: 13, color: '#555', marginBottom: 4, fontWeight: 600 }}
            >
              Serial bảo hành (nếu có)
            </Typography>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Nhập serial bảo hành (nếu có)"
              value={replacementSerial}
              onChange={e => setReplacementSerial(e.target.value)}
            />
          </Grid>
        </Grid>

        <Box mb={2}>
          <Typography
            variant="subtitle2"
            style={{ fontSize: 13, color: '#555', marginBottom: 4, fontWeight: 600 }}
          >
            Ghi chú / Lý do bảo hành
          </Typography>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Nhập ghi chú hoặc nguyên nhân lỗi..."
            value={note}
            onChange={e => setNote(e.target.value)}
            multiline
            rows={3}
          />
        </Box>

        {error && renderError(error)}
      </DialogContent>

      <DialogActions style={{ padding: '12px 24px', backgroundColor: '#fafafa' }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
          style={{ fontWeight: 600, textTransform: 'none' }}
        >
          Hủy
        </Button>
        <Button
          variant="contained"
          style={{
            backgroundColor: '#D9042F',
            color: '#fff',
            fontWeight: 700,
            textTransform: 'none',
            padding: '6px 20px'
          }}
          onClick={handleCreate}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Tạo phiếu bảo hành
        </Button>
      </DialogActions>
    </Dialog>
  );
}
